import { useCallback, useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCw, Search, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import { useRegion } from "@/contexts/RegionContext";
import { sumsubHub } from "@/lib/api";
import { SumsubList } from "./SumsubList";
import { SumsubDetail } from "./SumsubDetail";
import { ANSWER_TONE, STATUS_LABEL, fmtTs } from "./sumsubShared";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const LIMIT = 50;
const EMPTY = { q: "", status: "", answer: "", level: "", risk: "", date_from: "", date_to: "" };

function StatChip({ label, value, tone, active, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} className={`rounded-lg border px-3 py-2 text-left transition ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-slate-400"}`} data-testid={testId}>
      <div className={`text-[10px] uppercase tracking-wide ${active ? "text-slate-300" : "text-slate-500"}`}>{label}</div>
      <div className={`font-mono text-lg font-bold tabular-nums ${active ? "text-white" : tone || "text-slate-900"}`}>{value ?? 0}</div>
    </button>
  );
}

export default function SumsubHub() {
  const { currentRegion } = useRegion();
  const [filters, setFilters] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => { const t = setTimeout(() => { setFilters((f) => ({ ...f, q })); setOffset(0); }, 350); return () => clearTimeout(t); }, [q]);

  const load = useCallback(async (sync = true) => {
    setLoading(true);
    try { setData(await sumsubHub.list({ region: currentRegion, ...filters, limit: LIMIT, offset, sync })); }
    catch (e) { toast.error(errorDetailMessage(e, e.message)); }
    finally { setLoading(false); }
  }, [currentRegion, filters, offset]);
  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try { const r = await sumsubHub.sync(); toast.success(`Synced ${r.total} applicants from Sumsub in ${r.duration_s}s`); await load(false); }
    catch (e) { toast.error(errorDetailMessage(e, e.message)); }
    finally { setSyncing(false); }
  };
  const set = (k, v) => { setFilters((f) => ({ ...f, [k]: v === "any" ? "" : v })); setOffset(0); };
  const facets = data?.facets || {};
  const levels = useMemo(() => Object.keys(facets.level_name || {}).filter((l) => l && l !== "None").sort(), [facets.level_name]);
  const active = Object.entries(filters).filter(([k, v]) => v && k !== "q").length;
  const st = facets.review_status || {}, an = facets.review_answer || {};

  return (
    <div className="space-y-3" data-testid="sumsub-hub">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="w-5 h-5 text-emerald-600" />
        <h2 className="text-base font-semibold text-slate-900">Sumsub · {currentRegion === "AUS" ? "Australia" : "UK"} onboarding checks</h2>
        <span className="text-xs text-slate-500" data-testid="sumsub-scope">{data?.scope_total ?? "…"} applicants{data?.last_sync ? ` · synced ${fmtTs(data.last_sync)}` : ""}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8" onClick={syncNow} disabled={syncing} data-testid="sumsub-sync-btn">
            {syncing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}Sync from Sumsub
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 md:grid-cols-6" data-testid="sumsub-stat-strip">
        <StatChip label="All" value={data?.scope_total} active={!filters.status && !filters.answer} onClick={() => { set("status", ""); set("answer", ""); }} testId="sumsub-stat-all" />
        <StatChip label="Started" value={st.init} tone="text-sky-700" active={filters.status === "init"} onClick={() => set("status", filters.status === "init" ? "" : "init")} testId="sumsub-stat-init" />
        <StatChip label="Pending / queued" value={(st.pending || 0) + (st.queued || 0) + (st.prechecked || 0)} tone="text-violet-700" active={filters.status === "pending"} onClick={() => set("status", filters.status === "pending" ? "" : "pending")} testId="sumsub-stat-pending" />
        <StatChip label="On hold" value={st.onHold} tone="text-amber-700" active={filters.status === "onHold"} onClick={() => set("status", filters.status === "onHold" ? "" : "onHold")} testId="sumsub-stat-onhold" />
        <StatChip label="Approved" value={an.GREEN} tone="text-emerald-700" active={filters.answer === "GREEN"} onClick={() => set("answer", filters.answer === "GREEN" ? "" : "GREEN")} testId="sumsub-stat-green" />
        <StatChip label="Rejected" value={an.RED} tone="text-red-700" active={filters.answer === "RED"} onClick={() => set("answer", filters.answer === "RED" ? "" : "RED")} testId="sumsub-stat-red" />
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm" data-testid="sumsub-filters">
        <div className="relative w-72">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, phone, company, applicant ID" className="h-8 pl-8 text-xs" data-testid="sumsub-search" />
        </div>
        <Select value={filters.status || "any"} onValueChange={(v) => set("status", v)}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="sumsub-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="any">Any status</SelectItem>{Object.entries(STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.answer || "any"} onValueChange={(v) => set("answer", v)}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="sumsub-filter-answer"><SelectValue placeholder="Result" /></SelectTrigger>
          <SelectContent><SelectItem value="any">Any result</SelectItem>{Object.keys(ANSWER_TONE).filter((k) => k !== "none").map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}<SelectItem value="none">No result yet</SelectItem></SelectContent>
        </Select>
        <Select value={filters.level || "any"} onValueChange={(v) => set("level", v)}>
          <SelectTrigger className="h-8 w-56 text-xs" data-testid="sumsub-filter-level"><SelectValue placeholder="Level" /></SelectTrigger>
          <SelectContent><SelectItem value="any">{currentRegion === "AUS" ? "AU levels" : "UK level"} (default)</SelectItem><SelectItem value="all">All levels in the account</SelectItem>{levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.risk || "any"} onValueChange={(v) => set("risk", v)}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="sumsub-filter-risk"><SelectValue placeholder="Risk" /></SelectTrigger>
          <SelectContent><SelectItem value="any">Any risk</SelectItem><SelectItem value="yes">Has risk labels</SelectItem></SelectContent>
        </Select>
        <Input type="date" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} className="h-8 w-36 text-xs" data-testid="sumsub-filter-from" />
        <span className="text-xs text-slate-400">to</span>
        <Input type="date" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} className="h-8 w-36 text-xs" data-testid="sumsub-filter-to" />
        {(active > 0 || q) && (
          <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={() => { setFilters(EMPTY); setQ(""); setOffset(0); }} data-testid="sumsub-filters-clear"><X className="w-3.5 h-3.5 mr-1" />Clear</Button>
        )}
        {loading && <Loader2 className="ml-auto w-4 h-4 animate-spin text-slate-400" data-testid="sumsub-loading" />}
      </div>

      <SumsubList data={data} loading={loading} offset={offset} limit={LIMIT} onPage={setOffset} onOpen={setOpenId} />
      <SumsubDetail applicantId={openId} onClose={() => setOpenId(null)} onChanged={() => load(false)} />
    </div>
  );
}
